import type { SitePhotoId } from '../content/media-manifest';
import type { Locale } from '../i18n/locale';
import type { KitAssetId } from '../three/catalog';

/**
 * HOME content per locale, identical keys. Spanish uses formal "usted"; English is written as
 * native B2B copy, not translated sentence by sentence. Positioning: COLESCOMEX is the external
 * procurement and supply-chain arm of structured Colombian companies (launch product COLESCOMEX
 * SUPPLY); China and the United States are origins, not the brand. Sources: docs/phase-0/03,
 * docs/bos/03 (ICP), docs/bos/04 (positioning). No figures, client names, savings, transit times,
 * guarantees or infrastructure that is not verified.
 */
type Lines = readonly string[];
export interface HomeContent {
  hero: { eyebrow: string; title: Lines; lead: Lines; primary: string; secondary: string; caption: string };
  threads: { before: { title: Lines; body: string }; after: { title: Lines; body: string }; company: string; delivery: string; actors: readonly string[]; actorsLabel: string };
  need: { title: Lines; body: string; examplesLabel: string; examples: readonly string[]; field: string; placeholder: string; action: string };
  supply: { label: string; line: string; capabilities: readonly string[] };
  process: { title: string; body: string; steps: readonly { title: string; body: string }[] };
  sourcing: {
    eyebrow: string; title: Lines; body: Lines; photoAlt: string; dossier: string; of: string; verified: string; pending: string;
    checks: readonly { title: string; body: string }[]; gatesTitle: string; gatesBody: string; gates: readonly string[];
  };
  economics: { title: Lines; body: string; seems: { label: string; parts: readonly string[] }; is: { label: string; parts: readonly string[] }; note: Lines };
  journey: { title: Lines; legs: readonly { place: string; title: string }[] };
  industries: { title: Lines; body: string; items: readonly { name: string; body: string; alt: string }[]; others: { name: string; body: string; action: string; alt: string } };
  visibility: {
    title: Lines; body: string; note: string; project: string; status: string; documentsLabel: string; documents: readonly string[];
    milestones: readonly string[]; states: { done: string; active: string; next: string };
  };
  close: { title: string; body: Lines; composer: string; placeholder: Lines; action: string; secondary: string; nextLabel: string; next: readonly string[] };
}

/**
 * Process object map — single source of truth, one distinct canonical kit asset per step, chosen in the
 * real renderer for what it communicates without its title. `zoom` is per-asset framing (1 = the
 * object's bounding sphere fills the stage's normal volume); `turn` is its resting yaw.
 */
export const STEP_OBJECTS: readonly { object: KitAssetId; turn: number; zoom?: number }[] = [
  { object: '3D_08_INDUSTRIAL_COMPONENT_GENERIC', turn: 0.2, zoom: 1.15 },    // 01 need: the item the business needs
  { object: '3D_23_APPROVED_REFERENCE_SAMPLE', turn: -0.45, zoom: 1.1 },      // 02 specification: the reference that defines it
  { object: '3D_31_CONTROL_MONITOR', turn: 0.35, zoom: 1.2 },                 // 03 sourcing: desk research and supplier evaluation
  { object: '3D_25_FACTORY_NODE', turn: 0.45, zoom: 1.05 },                   // 04 verification: the supplier's site
  { object: '3D_20_TECHNICAL_EVIDENCE_FOLDER', turn: -0.6, zoom: 1.25 },      // 05 compliance: documentation and evidence
  { object: '3D_03_WOODEN_EXPORT_CRATE', turn: 0.5, zoom: 1.0 },              // 06 procurement: purchased goods, export-packed
  { object: '3D_18_MICROMETER', turn: -0.4, zoom: 1.35 },                      // 07 quality control: precision inspection
  { object: '3D_06_WRAPPED_PALLET', turn: 0.55, zoom: 1.0 },                  // 08 warehousing: consolidated cargo
  { object: '3D_11_CONTAINER_VESSEL_SIMPLIFIED', turn: -0.6, zoom: 1.55 },    // 09 international logistics
  { object: '3D_28_CUSTOMS_ARRIVAL_NODE', turn: 0.5, zoom: 1.05 },            // 10 import clearance: customs arrival
  { object: '3D_09_CONTAINER_TRUCK', turn: -0.65, zoom: 1.3 },                // 11 delivery: final handoff
];
export const SOURCING_PHOTO: SitePhotoId = 'B02_06';
export const INDUSTRY_PHOTOS: readonly SitePhotoId[] = ['B05_18', 'B05_19', 'B05_20'];
export const OTHERS_PHOTO: SitePhotoId = 'B05_17';
export const MILESTONE_STATES: readonly ('done' | 'active' | 'next')[] = ['done', 'done', 'done', 'active', 'next', 'next'];

export const HOME: Record<Locale, HomeContent> = {
  'es-CO': {
    hero: {
      eyebrow: 'Procurement · Supply chain · Comercio exterior',
      title: ['Su compra internacional.', 'Un solo responsable.'],
      lead: [
        'Para proyectos, CAPEX e insumos estratégicos.',
        'COLESCOMEX estructura el requerimiento, busca y verifica proveedores, valida viabilidad, compra, controla la calidad, importa y entrega en Colombia bajo una sola relación comercial.',
      ],
      primary: 'Iniciar proyecto',
      secondary: 'Ver cómo operamos',
      caption: 'Corredores ilustrativos hacia Colombia desde China, Estados Unidos y Europa.',
    },
    threads: {
      before: {
        title: ['Una sola compra no debería exigirle', 'coordinar ocho contrapartes.'],
        body: 'Cuando proveedor, inspección, logística, aduana, pagos y documentos operan por separado, su equipo absorbe la coordinación y el riesgo. COLESCOMEX integra la operación y responde por el conjunto.',
      },
      after: {
        title: ['Con COLESCOMEX,', 'una sola operación.'],
        body: 'Un requerimiento, una contraparte responsable, una historia operacional y una entrega en Colombia.',
      },
      company: 'Su empresa',
      delivery: 'Entrega en Colombia',
      actors: ['Proveedor', 'Inspección', 'Bodega', 'Agente de carga', 'Aduana', 'Transporte', 'Pagos', 'Documentos'],
      actorsLabel: 'Contrapartes de una compra internacional',
    },
    need: {
      title: ['Díganos qué necesita.', 'Nosotros estructuramos la compra.'],
      body: 'No necesita llegar con una ficha técnica perfecta. Partimos de su necesidad de negocio y la convertimos en un requerimiento que pueda cotizarse, verificarse, comprarse y entregarse.',
      examplesLabel: 'Por ejemplo',
      examples: [
        'Equipar una apertura o renovación hotelera',
        'Comprar una máquina o equipo que no se consigue localmente',
        'Abastecer un proyecto adjudicado con bienes de origen internacional',
        'Encontrar una segunda fuente para un insumo o componente recurrente',
      ],
      field: '¿Qué necesita comprar su empresa?',
      placeholder: 'Describa el producto, la cantidad y dónde lo necesita',
      action: 'Iniciar proyecto',
    },
    supply: {
      label: 'COLESCOMEX SUPPLY',
      line: 'Una sola operación activa las capacidades necesarias según el requerimiento.',
      capabilities: ['Estructuración del requerimiento', 'Supplier Intelligence', 'Viabilidad y cumplimiento', 'Procurement', 'Control de calidad', 'Operaciones en origen', 'Logística internacional', 'Importación y entrega'],
    },
    process: {
      title: 'Cómo operamos.',
      body: 'Once pasos que usted ve avanzar. Una sola operación que nosotros coordinamos.',
      steps: [
        { title: 'Necesidad', body: 'Entendemos qué necesita, para qué, en qué cantidad y para cuándo.' },
        { title: 'Especificación', body: 'Convertimos la necesidad en un requerimiento técnico y comercial que pueda compararse y comprarse.' },
        { title: 'Búsqueda de proveedores', body: 'Buscamos alternativas en China, Estados Unidos y otros orígenes según la operación.' },
        { title: 'Verificación del proveedor', body: 'Validamos identidad, sitio, capacidad y evidencia antes de recomendar.' },
        { title: 'Cumplimiento', body: 'Revisamos los requisitos aplicables antes de comprometer la compra.' },
        { title: 'Compra', body: 'Negociamos condiciones y estructuramos la operación bajo controles definidos.' },
        { title: 'Control de calidad', body: 'Coordinamos inspección y evidencia según producto, proveedor y riesgo.' },
        { title: 'Bodega y consolidación', body: 'Recibimos, verificamos, almacenamos o consolidamos en origen cuando la operación lo requiere.' },
        { title: 'Transporte internacional', body: 'Elegimos y coordinamos la modalidad según volumen, plazo, costo y riesgo.' },
        { title: 'Nacionalización', body: 'Coordinamos los actores autorizados y la documentación necesaria para el ingreso a Colombia.' },
        { title: 'Entrega', body: 'Entregamos en el destino acordado y cerramos la operación con evidencia.' },
      ],
    },
    sourcing: {
      eyebrow: 'Control en origen',
      title: ['Antes de comprar,', 'verificamos.'],
      body: [
        'China, Estados Unidos u otro origen: la decisión empieza por la operación, no por el país.',
        'Verificamos proveedor, sitio, capacidad, calidad, documentación y condiciones antes de comprometer capital.',
      ],
      photoAlt: 'Auditoría en planta de un proveedor: revisión de una línea de producción.',
      dossier: 'Expediente de proveedor',
      of: 'de',
      verified: 'verificado',
      pending: 'pendiente',
      checks: [
        { title: 'Identidad', body: 'La entidad existe y coincide con quien vende.' },
        { title: 'Sitio y fábrica', body: 'Verificamos dónde y cómo se produce o suministra.' },
        { title: 'Capacidad', body: 'Puede responder al volumen y al plazo.' },
        { title: 'Calidad', body: 'Definimos muestras, criterios e inspección según el riesgo.' },
        { title: 'Evidencia', body: 'Cada hallazgo relevante queda documentado.' },
        { title: 'Términos', body: 'Precio, Incoterm, pago, garantía y condiciones por escrito.' },
      ],
      gatesTitle: 'Control antes de mover.',
      gatesBody: 'La carga avanza cuando cada condición está cumplida.',
      gates: ['Proveedor verificado', 'Viabilidad revisada', 'Calidad controlada', 'Condiciones de embarque cumplidas'],
    },
    economics: {
      title: ['El precio de compra', 'no es el costo de la decisión.'],
      body: 'Antes de cotizar, construimos la economía completa de la operación: producto, origen, calidad, logística, seguro, nacionalización, impuestos y entrega.',
      seems: { label: 'Lo visible', parts: ['Precio de compra', 'Flete'] },
      is: { label: 'El costo total de la operación', parts: ['Producto', 'Gastos en origen', 'Control de calidad', 'Bodega y consolidación', 'Flete internacional', 'Seguro', 'Nacionalización', 'Impuestos', 'Última milla'] },
      note: ['Componentes ilustrativos.', 'Cada operación se calcula con sus propios valores.'],
    },
    journey: {
      title: ['Del origen a Colombia,', 'con control sobre cada hito.'],
      legs: [
        { place: 'Origen', title: 'China, Estados Unidos u otro mercado aprobado: proveedor, documentación y condiciones verificadas antes del embarque.' },
        { place: 'En tránsito', title: 'Marítimo, aéreo o multimodal, con seguimiento por hitos y gestión de excepciones.' },
        { place: 'Colombia', title: 'Llegada y nacionalización coordinadas con los actores autorizados.' },
        { place: 'Su destino', title: 'Entrega acordada y cierre documental de la operación.' },
      ],
    },
    industries: {
      title: ['Compras internacionales', 'donde la ejecución importa.'],
      body: 'Trabajamos especialmente con empresas que manejan compras planificadas, especificaciones exigentes, CAPEX, fechas de proyecto o abastecimiento recurrente.',
      items: [
        { name: 'Hotelería y FF&E', body: 'Mobiliario, iluminación, herrajes, cocinas, áreas comunes, exteriores y otros paquetes FF&E/OS&E para aperturas, renovaciones y expansión.', alt: 'Equipo revisando muestras de acabados para un proyecto hotelero.' },
        { name: 'Energía e infraestructura eléctrica', body: 'Equipos y componentes para proyectos solares, eléctricos e infraestructura, sujetos a la revisión técnica y regulatoria aplicable.', alt: 'Técnicos revisando un tablero eléctrico en planta.' },
        { name: 'Construcción y acabados', body: 'Acabados, grifería, iluminación, perfiles, sistemas livianos, herramientas y materiales para proyectos y abastecimiento recurrente.', alt: 'Revisión de planos y materiales en una obra en construcción.' },
      ],
      others: { name: 'Otras compras B2B', body: 'Maquinaria, tecnología, componentes, equipos e insumos para operaciones con compras planificadas y suficiente valor para estructurar una operación internacional.', action: 'Cuéntenos su caso', alt: 'Entrega B2B de un equipo industrial a un cliente.' },
    },
    visibility: {
      title: ['Una operación.', 'Una sola fuente de verdad.'],
      body: 'Hitos, documentos, decisiones y excepciones quedan conectados para que Compras, Proyectos, Finanzas y Gerencia sepan qué está pasando sin reconstruir la operación entre correos, chats y archivos.',
      note: 'Vista ilustrativa del portal de clientes.',
      project: 'Proyecto de ejemplo: dotación de habitaciones',
      status: 'En tránsito marítimo',
      documentsLabel: 'Documentos',
      documents: ['Orden de compra', 'Informe de inspección', 'Documento de transporte'],
      milestones: ['Requerimiento recibido', 'Proveedor verificado', 'Calidad controlada', 'En tránsito', 'Nacionalización', 'Entrega'],
      states: { done: 'Completado', active: 'En curso', next: 'Pendiente' },
    },
    close: {
      title: '¿Qué necesita comprar su empresa?',
      body: ['Cuéntenos dónde está hoy.', 'Podemos partir de una necesidad, una especificación o una cotización que ya tenga.'],
      composer: 'Cuéntenos el requerimiento',
      placeholder: ['Producto, cantidad, uso o proyecto, fecha requerida y lugar de entrega.', 'Escriba lo que ya sabe; nosotros estructuramos el resto.'],
      action: 'Iniciar proyecto',
      secondary: 'Hablar con COLESCOMEX',
      nextLabel: 'Qué sigue',
      next: ['Revisamos el requerimiento', 'Identificamos información faltante y viabilidad inicial', 'Definimos el siguiente paso de la operación'],
    },
  },
  en: {
    hero: {
      eyebrow: 'Procurement · Supply chain · International trade',
      title: ['Your international procurement.', 'One accountable partner.'],
      lead: [
        'For projects, CAPEX and strategic inputs.',
        'COLESCOMEX structures the requirement, sources and verifies suppliers, validates feasibility, purchases, manages quality, imports and delivers in Colombia under one commercial relationship.',
      ],
      primary: 'Start a project',
      secondary: 'See how we operate',
      caption: 'Illustrative corridors into Colombia from China, the United States and Europe.',
    },
    threads: {
      before: {
        title: ['One purchase should not require your team', 'to coordinate eight counterparties.'],
        body: 'When suppliers, inspections, logistics, customs, payments and documents operate separately, your team absorbs the coordination burden and the risk. COLESCOMEX integrates the operation and takes responsibility for the whole.',
      },
      after: {
        title: ['With COLESCOMEX,', 'it becomes one operation.'],
        body: 'One requirement, one accountable partner, one operational history and one delivery in Colombia.',
      },
      company: 'Your company',
      delivery: 'Delivery in Colombia',
      actors: ['Supplier', 'Inspection', 'Warehouse', 'Freight forwarder', 'Customs', 'Transport', 'Payments', 'Documents'],
      actorsLabel: 'Counterparties in an international purchase',
    },
    need: {
      title: ['Tell us what you need.', 'We structure the purchase.'],
      body: 'You do not need a perfect technical specification to begin. We start from the business need and turn it into a requirement that can be quoted, verified, purchased and delivered.',
      examplesLabel: 'For example',
      examples: [
        'Equip a hotel opening or renovation',
        'Source machinery or equipment not available locally',
        'Supply an awarded project with internationally sourced goods',
        'Find a second source for a recurring input or component',
      ],
      field: 'What does your company need to purchase?',
      placeholder: 'Describe the product, quantity and where you need it',
      action: 'Start a project',
    },
    supply: {
      label: 'COLESCOMEX SUPPLY',
      line: 'One operation activates the capabilities required by the purchase.',
      capabilities: ['Requirement structuring', 'Supplier Intelligence', 'Feasibility & compliance', 'Procurement', 'Quality control', 'Origin operations', 'International logistics', 'Import & delivery'],
    },
    process: {
      title: 'How we operate.',
      body: 'Eleven steps you can follow. One operation we coordinate end to end.',
      steps: [
        { title: 'Need', body: 'We understand what you need, why you need it, how much and by when.' },
        { title: 'Specification', body: 'We turn the need into a technical and commercial requirement that can be compared and purchased.' },
        { title: 'Supplier sourcing', body: 'We search for alternatives in China, the United States and other origins according to the operation.' },
        { title: 'Supplier verification', body: 'We validate identity, site, capability and evidence before making a recommendation.' },
        { title: 'Compliance', body: 'We review applicable requirements before the purchase is committed.' },
        { title: 'Procurement', body: 'We negotiate terms and structure the purchase under defined controls.' },
        { title: 'Quality control', body: 'We coordinate inspection and evidence according to product, supplier and risk.' },
        { title: 'Warehousing & consolidation', body: 'We receive, verify, store or consolidate at origin when the operation requires it.' },
        { title: 'International logistics', body: 'We select and coordinate the mode according to volume, timing, cost and risk.' },
        { title: 'Import clearance', body: 'We coordinate the authorized parties and documentation required for entry into Colombia.' },
        { title: 'Delivery', body: 'We deliver to the agreed destination and close the operation with supporting evidence.' },
      ],
    },
    sourcing: {
      eyebrow: 'Control at origin',
      title: ['Before we buy,', 'we verify.'],
      body: [
        'China, the United States or another origin: the decision starts with the operation, not the country.',
        'We verify supplier identity, site, capability, quality, documentation and commercial terms before capital is committed.',
      ],
      photoAlt: 'On-site supplier audit: a production line under review.',
      dossier: 'Supplier file',
      of: 'of',
      verified: 'verified',
      pending: 'pending',
      checks: [
        { title: 'Identity', body: 'The legal entity exists and matches the seller.' },
        { title: 'Site & factory', body: 'We verify where and how the product is made or supplied.' },
        { title: 'Capability', body: 'The supplier can respond to the required volume and timing.' },
        { title: 'Quality', body: 'We define samples, criteria and inspection according to risk.' },
        { title: 'Evidence', body: 'Each material finding is documented.' },
        { title: 'Terms', body: 'Price, Incoterm, payment, warranty and conditions are documented.' },
      ],
      gatesTitle: 'Control before anything moves.',
      gatesBody: 'Cargo moves forward once each condition is met.',
      gates: ['Supplier verified', 'Feasibility reviewed', 'Quality controlled', 'Shipment conditions met'],
    },
    economics: {
      title: ['The purchase price', 'is not the cost of the decision.'],
      body: 'Before we quote, we build the full economics of the operation: product, origin costs, quality, logistics, insurance, import clearance, taxes and delivery.',
      seems: { label: 'What you see first', parts: ['Purchase price', 'Freight'] },
      is: { label: 'The full cost of the operation', parts: ['Product', 'Origin costs', 'Quality control', 'Warehousing & consolidation', 'International freight', 'Insurance', 'Import clearance', 'Taxes', 'Final mile'] },
      note: ['Illustrative components.', 'Each operation is calculated using its own actual values.'],
    },
    journey: {
      title: ['From origin to Colombia,', 'with control over every milestone.'],
      legs: [
        { place: 'Origin', title: 'China, the United States or another approved market: supplier, documentation and terms verified before shipment.' },
        { place: 'In transit', title: 'Ocean, air or multimodal, with milestone tracking and exception management.' },
        { place: 'Colombia', title: 'Arrival and import clearance coordinated with the authorized parties.' },
        { place: 'Your destination', title: 'Agreed delivery and documented operational closeout.' },
      ],
    },
    industries: {
      title: ['International procurement', 'where execution matters.'],
      body: 'We work especially with companies managing planned purchases, demanding specifications, CAPEX, project deadlines or recurring supply.',
      items: [
        { name: 'Hospitality & FF&E', body: 'Furniture, lighting, hardware, kitchens, common areas, outdoor items and other FF&E/OS&E packages for openings, renovations and expansion.', alt: 'A team reviewing finish samples for a hotel project.' },
        { name: 'Energy & electrical infrastructure', body: 'Equipment and components for solar, electrical and infrastructure projects, subject to the applicable technical and regulatory review.', alt: 'Technicians reviewing an electrical switchboard on site.' },
        { name: 'Construction & finishes', body: 'Finishes, plumbing fixtures, lighting, profiles, lightweight systems, tools and materials for projects and recurring supply.', alt: 'Reviewing drawings and materials on a construction site.' },
      ],
      others: { name: 'Other B2B procurement', body: 'Machinery, technology, components, equipment and inputs for planned purchases with enough value to structure an international operation.', action: 'Tell us about your case', alt: 'B2B handover of industrial equipment to a client.' },
    },
    visibility: {
      title: ['One operation.', 'One source of truth.'],
      body: 'Milestones, documents, decisions and exceptions stay connected so Procurement, Projects, Finance and Management can see what is happening without rebuilding the operation from emails, chats and files.',
      note: 'Illustrative client portal view.',
      project: 'Sample project: guest room package',
      status: 'In ocean transit',
      documentsLabel: 'Documents',
      documents: ['Purchase order', 'Inspection report', 'Transport document'],
      milestones: ['Requirement received', 'Supplier verified', 'Quality controlled', 'In transit', 'Import clearance', 'Delivery'],
      states: { done: 'Completed', active: 'In progress', next: 'Pending' },
    },
    close: {
      title: 'What does your company need to purchase?',
      body: ['Tell us where you are today.', 'We can start from a business need, a specification or a quotation you already have.'],
      composer: 'Tell us about the requirement',
      placeholder: ['Product, quantity, use or project, required date and delivery location.', 'Share what you already know; we will structure the rest.'],
      action: 'Start a project',
      secondary: 'Talk to COLESCOMEX',
      nextLabel: 'What happens next',
      next: ['We review the requirement', 'We identify missing information and initial feasibility', 'We define the next step for the operation'],
    },
  },
};
